import { type QuothaPanelEvidenceItem, type QuothaPanelPayload } from '../api'

const normalizePanelText = (value?: unknown): string | null => {
  if (typeof value === 'number') return value.toString()
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const normalizeStringArray = (value?: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizePanelText(item))
      .filter((item): item is string => Boolean(item))
  }
  const single = normalizePanelText(value)
  return single ? [single] : []
}

const normalizeEvidenceItem = (item: unknown): QuothaPanelEvidenceItem | null => {
  if (!item) return null

  if (typeof item !== 'object') {
    const single = normalizePanelText(item)
    return single ? { title: single } : null
  }

  const data = item as Record<string, unknown>
  const title =
    normalizePanelText(data.title) ??
    normalizePanelText((data as { label?: unknown }).label) ??
    normalizePanelText((data as { heading?: unknown }).heading) ??
    normalizePanelText((data as { name?: unknown }).name)
  const url = normalizePanelText((data as { url?: unknown }).url)
  const snippet =
    normalizePanelText((data as { snippet?: unknown }).snippet) ??
    normalizePanelText((data as { summary?: unknown }).summary)
  const note = normalizePanelText((data as { note?: unknown }).note)

  if (!title && !url && !snippet && !note) return null

  return {
    title: title ?? url ?? snippet ?? null,
    url: url ?? null,
    snippet: snippet ?? null,
    note,
  }
}

export const normalizeQuothaPanelPayload = (panel: unknown): QuothaPanelPayload | null => {
  if (!panel || typeof panel !== 'object') return null

  const data = panel as Record<string, unknown>

  const evidence = Array.isArray((data as { evidence?: unknown }).evidence)
    ? ((data as { evidence: unknown[] }).evidence as unknown[])
        .map((item) => normalizeEvidenceItem(item))
        .filter((item): item is QuothaPanelEvidenceItem => Boolean(item))
    : []

  const bullets = normalizeStringArray((data as { bullets?: unknown }).bullets)

  const gapSources: unknown[] = []
  if (data.gaps !== undefined) gapSources.push(data.gaps)
  if ((data as { gap_list?: unknown }).gap_list !== undefined) gapSources.push((data as { gap_list?: unknown }).gap_list)
  if ((data as { missing?: unknown }).missing !== undefined) gapSources.push((data as { missing?: unknown }).missing)
  if (bullets.length) gapSources.push(bullets)

  const gaps = gapSources.flatMap((value) => normalizeStringArray(value))

  const normalized: QuothaPanelPayload = {
    title:
      normalizePanelText(data.title) ??
      normalizePanelText((data as { heading?: unknown }).heading) ??
      normalizePanelText((data as { label?: unknown }).label),
    verdict: normalizePanelText((data as { verdict?: unknown }).verdict),
    support: normalizePanelText((data as { support?: unknown }).support),
    micro_commentary: normalizePanelText((data as { micro_commentary?: unknown }).micro_commentary),
    evidence_basis: normalizePanelText((data as { evidence_basis?: unknown }).evidence_basis),
    strongest_support: normalizePanelText((data as { strongest_support?: unknown }).strongest_support),
    needs_caution: normalizePanelText((data as { needs_caution?: unknown }).needs_caution),
    speculative_or_reconstructed: normalizePanelText(
      (data as { speculative_or_reconstructed?: unknown }).speculative_or_reconstructed,
    ),
    what_would_improve_confidence: normalizePanelText(
      (data as { what_would_improve_confidence?: unknown }).what_would_improve_confidence,
    ),
    assessment:
      normalizePanelText((data as { assessment?: unknown }).assessment) ??
      normalizePanelText((data as { body?: unknown }).body) ??
      normalizePanelText((data as { summary?: unknown }).summary) ??
      normalizePanelText((data as { text?: unknown }).text) ??
      normalizePanelText((data as { content?: unknown }).content),
    summary: normalizePanelText((data as { summary?: unknown }).summary),
    evidence: evidence.length ? evidence : undefined,
    gaps: gaps.length ? gaps : undefined,
    notes: normalizePanelText((data as { notes?: unknown }).notes) ?? normalizePanelText((data as { note?: unknown }).note),
    body: normalizePanelText((data as { body?: unknown }).body),
    bullets: bullets.length ? bullets : undefined,
    meta:
      typeof (data as { meta?: unknown }).meta === 'object' && (data as { meta?: unknown }).meta !== null
        ? ((data as { meta?: Record<string, unknown> }).meta as Record<string, unknown> | null)
        : null,
  }

  const hasContent =
    Boolean(normalized.title) ||
    Boolean(normalized.verdict) ||
    Boolean(normalized.support) ||
    Boolean(normalized.micro_commentary) ||
    Boolean(normalized.evidence_basis) ||
    Boolean(normalized.strongest_support) ||
    Boolean(normalized.needs_caution) ||
    Boolean(normalized.speculative_or_reconstructed) ||
    Boolean(normalized.what_would_improve_confidence) ||
    Boolean(normalized.assessment) ||
    Boolean(normalized.summary) ||
    Boolean(normalized.notes) ||
    (normalized.evidence && normalized.evidence.length > 0) ||
    (normalized.gaps && normalized.gaps.length > 0) ||
    (normalized.bullets && normalized.bullets.length > 0)

  if (!hasContent) return null

  return normalized
}
